import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { useApi } from "@/api/hooks/useApi";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Button } from "@/components/Button/Button";
import { UploadFileButton } from "@/components/Button/UploadFileButton";
import { DataTable } from "@/components/DataTable/DataTable";
import type { Column, Data } from "@/components/DataTable/parser";
import { parseData } from "@/components/DataTable/parser";
import { downloadExcel, parseExcel } from "@/components/DataTable/sheet";
import { validate } from "@/components/DataTable/validation";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { formatAddress } from "@/helpers/address";
import { parseFileContent } from "@/helpers/file-parse";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useOnDropFiles } from "@/hooks/useOnDropFiles";
import { useOnPaste } from "@/hooks/useOnPaste";
import { useSlug } from "@/hooks/useSlug";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { useCompanyQueries } from "@/queries/companies/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function CompanyBulkUploadPage(): React.ReactNode {
  const [isUploading, setIsUploading] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [data, setData] = useState<Data<NonNullable<typeof columns>>>();

  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const api = useApi();
  const slug = useSlug();
  const showFlashToast = useFlashToast();

  const { mutateAsync: uploadCompanyAsync } = useMutation({ mutationFn: api.postCompaniesV1 });
  const addressQueries = useAddressQueries();
  const {
    data: addresses,
    isLoading: isLoadingAddresses,
    error: addressesError,
  } = useQuery(addressQueries.getAddresses());
  const companyQueries = useCompanyQueries();
  const {
    data: companies,
    isLoading: isLoadingCompanies,
    error: companiesError,
  } = useQuery({
    ...companyQueries.getCompanies(),
    enabled: sessionUser.project.type === "companyBased",
  });

  const columns = useMemo(() => {
    if (!isDefined(addresses) || !isDefined(companies)) {
      return undefined;
    }

    return [
      {
        name: "Address",
        alias: ["adres", "streetname", "straatnaam", "straat", "street"],
        rules: {
          required: t("page.company-bulk-upload.validation.address.required"),
          oneOf: {
            error: t("page.company-bulk-upload.validation.address.unknown"),
            values: addresses.items.map(formatAddress),
          },
        },
      },
      {
        name: "Company",
        alias: ["bedrijf", "naam", "name"],
        rules: {
          required: t("page.company-bulk-upload.validation.name.required"),
          notOneOf: {
            error: t("page.company-bulk-upload.validation.name.exists"),
            values: companies.items.map((x) => x.name),
          },
        },
      },
      {
        name: "Description",
        alias: ["content", "details", "beschrijving"],
      },
    ] as const satisfies readonly Column[];
  }, [t, addresses, companies]);

  async function upload() {
    if (!data) {
      throw new Error("Data missing");
    }

    if (!dataState?.valid) {
      throw new Error("Data is invalid");
    }

    try {
      setIsUploading(true);

      for (const row of data) {
        const idx = data.indexOf(row);
        updateValue(idx, "uploadStatus", "UPLOADING");
        const rowData = row as { [k in NonNullable<typeof columns>[number]["name"]]: string };
        try {
          const addressId = mapAddressValue(rowData["Address"]);
          if (!addressId) {
            throw new Error("Address not found");
          }

          await uploadCompanyAsync({
            addressId,
            name: rowData["Company"],
            description: rowData["Description"],
          });
          updateValue(idx, "uploadStatus", "SUCCESS");
        } catch {
          updateValue(idx, "uploadStatus", "ERROR");
        }
      }

      showFlashToast({
        title: t("page.company-bulk-upload.notification.success", { count: data.length }),
        type: "success",
      });
    } finally {
      setIsUploading(false);
    }
  }

  function mapAddressValue(addressName: string) {
    if (!addresses) {
      throw new Error("No addresses available yet");
    }

    for (const address of addresses.items) {
      if (addressName === formatAddress(address)) {
        return address.id;
      }
    }
  }

  function updateValue(row: number, column: string, value: string) {
    setData((oldData) => {
      if (!oldData) {
        throw new Error("No existing data");
      }

      const newData = [...oldData];
      const rowNumber = Number(row);
      const oldRow = oldData[rowNumber];
      const newRow = { ...oldRow, [column]: value };
      newData.splice(rowNumber, 1, newRow);

      return newData;
    });
  }

  function removeRow(row: number) {
    setData((oldData) => {
      if (!oldData) {
        return;
      }

      const newData = [...oldData];
      newData.splice(row, 1);

      // No more data remaining, reset form
      if (newData.length === 0) {
        return undefined;
      }

      return newData;
    });
  }

  const loadCsv = useCallback(
    async (...args: Parameters<typeof parseExcel>) => {
      try {
        setIsParsingFile(true);
        const rows = await parseExcel(...args);
        setData(parseData(rows as object[], columns!));
      } finally {
        setIsParsingFile(false);
      }
    },
    [columns],
  );

  useOnDropFiles(
    useCallback(
      async (files) => {
        const file = files?.[0].getAsFile();
        if (file) {
          try {
            const content = await parseFileContent(file);
            await loadCsv(content);
          } catch (e) {
            alert(e);
          }
        }
      },
      [loadCsv],
    ),
    !data && !isParsingFile,
  );

  useOnPaste(
    useCallback(
      (file) => {
        void loadCsv(file).catch((e) => alert(`Could not paste data: ${e}`));
      },
      [loadCsv],
    ),
    !data && !isParsingFile,
  );

  const onUploadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    const file = target.files?.[0];

    if (file) {
      try {
        const contents = await parseFileContent(file);
        await loadCsv(contents);
      } catch (e) {
        alert(e);
        target.value = "";
      }
    }
  };

  const dataState = useMemo(() => (data && columns ? validate(columns, data) : undefined), [data, columns]);
  const isBulkUploadSuccessful = data?.every((row) => row.uploadStatus === "SUCCESS");

  if (sessionUser.project.type === "addressBased") {
    return <ErrorPage error={t("page.company-bulk-upload.error.not-available")} />;
  }

  const isLoading = isLoadingAddresses || isLoadingCompanies;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const error = addressesError || companiesError;
  if (!columns || error) {
    return <ErrorPage error={error ?? t("page.company-bulk-upload.error.no-columns")} />;
  }

  return (
    <DocumentPaper
      title={t("page.company-bulk-upload.title")}
      subTitle={
        <Breadcrumbs
          pages={[
            {
              name: t("page.companies.list.title"),
              to: routes.companies.list({
                slug,
              }),
            },
            {
              name: t("page.company-bulk-upload.title"),
            },
          ]}
        />
      }
      actions={
        <>
          {data && isBulkUploadSuccessful && (
            <Button
              onClick={() => {
                setData(undefined);
              }}
            >
              {t("page.company-bulk-upload.reset")}
            </Button>
          )}
          {!data && (
            <UploadFileButton
              disabled={isUploading}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={onUploadFile}
            >
              {t("page.company-bulk-upload.button")}
            </UploadFileButton>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4 rounded-lg bg-white p-5">
        {data && (
          <div className="flex flex-col items-end gap-8">
            <DataTable
              data={data}
              columns={columns}
              dataState={dataState!}
              onRemoveRow={removeRow}
              onUpdateValue={updateValue}
              disabled={isUploading}
              data-testid="company-data-table"
            />
            {!isBulkUploadSuccessful && (
              <Button data-testid="submit-btn" onClick={upload} isLoading={isUploading} disabled={!dataState?.valid}>
                {t("page.company-bulk-upload.submit")}
              </Button>
            )}
          </div>
        )}
        {!data && isParsingFile && <FullSizeLoader withPadding />}{" "}
        {!data && !isParsingFile && (
          <>
            <p className="max-w-prose whitespace-pre-wrap">{t("page.company-bulk-upload.prose")}</p>
            <Button
              styling="ghostPrimary"
              icon={<Download01Icon />}
              onClick={() =>
                downloadExcel(`company-upload-${sessionUser.project.name}`, [
                  columns.map((x) => x.name),
                  ...(addresses?.items.map((x) => [formatAddress(x)]) || []),
                ])
              }
            >
              {t("page.company-bulk-upload.example-file")}
            </Button>
          </>
        )}
      </div>
    </DocumentPaper>
  );
}
